import { FC } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { mq, BreakpointSizes } from '@taleemabad/dsm/themes';
import {
  BaseOptionCard,
  CardStatus,
  Button,
  IconText,
  Radio,
  RadioOption,
  Spacing,
  StyledDiv,
  StyledFixedButtonWrapper,
  Typography,
} from '@taleemabad/dsm/components';

// Use lowercase to match backend values
export enum ObservationMode {
  Manual = 'manual',
  AiAssisted = 'aiAssisted',
}

export interface ObservationModeChoiceProps {
  selectedMode?: ObservationMode | '';
  onModeChange: (mode: ObservationMode) => void;
  onNext?: () => void;
  isNextDisabled?: boolean;
}

export const ObservationModeChoice: FC<ObservationModeChoiceProps> = ({
  selectedMode = '',
  onModeChange,
  onNext,
  isNextDisabled = false,
}) => {
  const manualSelected = selectedMode === ObservationMode.Manual;
  const aiSelected = selectedMode === ObservationMode.AiAssisted;

  const manualRadio: RadioOption = {
    value: ObservationMode.Manual,
    content: '',
    checked: manualSelected,
    dataCy: 'manual',
  };
  const aiRadio: RadioOption = {
    value: ObservationMode.AiAssisted,
    content: '',
    checked: aiSelected,
    dataCy: 'ai',
  };

  return (
    <StyledDiv flexDirection="column" gap={16}>
      <Spacing marginTop="sm">
        <Typography variant="textBodyLargeNeutral10">
          How would you like to conduct this observation?
        </Typography>
      </Spacing>

      <StyledCardsContainer>
        <BaseOptionCard
          option={{
            value: ObservationMode.Manual,
            content: 'Manual Observation',
          }}
          status={manualSelected ? CardStatus.Selected : CardStatus.Unselected}
          data-cy="observation-mode-manual"
        >
          <StyledCardRow>
            <IconText
              icon="edit"
              text="Manual Observation"
              textVariant="textTitleSmall"
            />
            <Radio
              option={manualRadio}
              checked={manualSelected}
              name="observation-mode"
              onSelect={() => onModeChange(ObservationMode.Manual)}
            />
          </StyledCardRow>
          <Spacing marginTop="xs">
            <Typography variant="textBodyMediumOnSurfaceVariant">
              Manually observe the class and mark indicators.
            </Typography>
          </Spacing>
        </BaseOptionCard>

        <BaseOptionCard
          option={{
            value: ObservationMode.AiAssisted,
            content: 'Recording-based Observation',
          }}
          status={aiSelected ? CardStatus.Selected : CardStatus.Unselected}
          data-cy="observation-mode-ai"
        >
          <StyledCardRow>
            <IconText
              icon="auto_awesome"
              text="Recording-based Observation"
              textVariant="textTitleSmall"
            />
            <Radio
              option={aiRadio}
              checked={aiSelected}
              name="observation-mode"
              onSelect={() => onModeChange(ObservationMode.AiAssisted)}
            />
          </StyledCardRow>
          <Spacing marginTop="xs">
            <Typography variant="textBodyMediumOnSurfaceVariant">
              Record class audio to generate your Class Insights automatically.
            </Typography>
          </Spacing>
        </BaseOptionCard>
      </StyledCardsContainer>

      <StyledFixedButtonWrapper bottom={0} zIndex={1}>
        <Button
          onClick={onNext}
          disabled={isNextDisabled || !selectedMode}
          data-cy="observation-mode-next-button"
        >
          Next
        </Button>
      </StyledFixedButtonWrapper>
    </StyledDiv>
  );
};

const StyledCardsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--rk-spacing-lg);
  ${mq(BreakpointSizes.desktop)} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StyledCardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rk-spacing-lg);
  ${css`
    label {
      margin-left: auto;
    }
  `}
`;
